import React from 'react';
import { ConnectorToolbar } from './ConnectorToolbar';
import { ShapeStyleToolbar } from './ShapeStyleToolbar';
import { ConnectorElement, EditableElement } from '../editing/types';
import { useTextEditor } from '../editing/useTextEditor';

type UseTextEditorReturn = ReturnType<typeof useTextEditor>;

interface CanvasToolbarsProps {
  isEditMode: boolean;
  selectedIds: string[];
  elements: EditableElement[];
  isTransforming: boolean;
  textEditor: UseTextEditorReturn;
  getElementsScreenBounds: (ids: string[]) => { x: number; y: number; width: number; height: number; centerX: number; centerY: number } | null;
  containerRef: React.RefObject<HTMLDivElement>;
  historyManagerRef: React.RefObject<any>;
}

export const CanvasToolbars: React.FC<CanvasToolbarsProps> = ({
  isEditMode,
  selectedIds,
  elements,
  isTransforming,
  textEditor,
  getElementsScreenBounds,
  containerRef,
  historyManagerRef,
}) => {
  // Connector Toolbar - hidden during drag/resize
  const connectorToolbar = isEditMode && selectedIds.length > 0 && !isTransforming && (() => {
    // Get connector elements (for data like stroke, arrowheads)
    const selectedConnectors = elements
      .filter((el) => selectedIds.includes(el.id) && el.type === 'connector')
      .map((el) => el as ConnectorElement);
    
    if (selectedConnectors.length === 0) return null;
    
    // Get position directly from Konva (source of truth)
    const connectorIds = selectedConnectors.map(c => c.id);
    const bounds = getElementsScreenBounds(connectorIds);
    if (!bounds) return null;
    
    return (
      <ConnectorToolbar
        connectors={selectedConnectors}
        onUpdate={(updates) => {
          selectedConnectors.forEach((connector) => {
            historyManagerRef.current?.modifyElement(connector.id, {
              data: { ...connector.data, ...updates },
            });
          });
        }}
        position={{ x: bounds.centerX, y: bounds.centerY }}
        elementBounds={{ 
          x: bounds.x, 
          y: bounds.y, 
          width: bounds.width, 
          height: bounds.height 
        }}
        containerRef={containerRef}
      />
    );
  })();

  // Shape Style Toolbar - hidden during drag/resize
  const shapeStyleToolbar = !textEditor.isEditing && !isTransforming && (() => {
    // Get shape elements (for data like fill, stroke)
    const selectedShapes = elements.filter(
      (el) => el.type === 'shape' && selectedIds.includes(el.id)
    );
    
    if (selectedShapes.length === 0) return null;
    
    // Get position directly from Konva (source of truth)
    const shapeIds = selectedShapes.map(s => s.id);
    const bounds = getElementsScreenBounds(shapeIds);
    if (!bounds) return null;
    
    return (
      <ShapeStyleToolbar
        shapes={selectedShapes}
        onUpdate={(updates) => {
          selectedShapes.forEach((shape) => {
            historyManagerRef.current?.modifyElement(shape.id, {
              data: { ...shape.data, ...updates },
            });
          });
        }}
        position={{ x: bounds.centerX, y: bounds.centerY }}
        elementBounds={{ 
          x: bounds.x, 
          y: bounds.y, 
          width: bounds.width, 
          height: bounds.height 
        }}
        containerRef={containerRef}
      />
    );
  })();

  return (
    <>
      {connectorToolbar}
      {shapeStyleToolbar}
    </>
  );
};
